import { useMemo } from 'react'

import { StatusBadge } from '@/components/status-badge/status-badge'

import { type CaseSearchItem } from '@/lib/queries/business/types'
import { cn } from '@/lib/utils'

interface Props {
  caseData: CaseSearchItem
  isNVCAssociatedCase: boolean
}

export const CellContentProcessingTime = ({ caseData, isNVCAssociatedCase }: Props) => {
  const {
    processingDays,
    inquirySubmitted,
    visaBulletinSubmitted: aosSubmitted,
    isAvailableRequest,
    is_end,
    isEligibleAOS,
    isPremium,
  } = caseData

  const badges = useMemo(() => {
    const result: React.ReactNode[] = []

    if (processingDays !== null && processingDays >= 0) {
      result.push(
        <ProcessingDaysBadge
          key="processing-days-badge"
          processingDays={processingDays}
          inquiryEligible={isAvailableRequest}
          caseEnded={is_end}
        />
      )
    }

    if (!is_end && (inquirySubmitted || isAvailableRequest !== null)) {
      result.push(<CaseInquiryBadge key="status-badge" submitted={inquirySubmitted} eligible={isAvailableRequest} />)
    }

    if (!isNVCAssociatedCase && (isEligibleAOS || aosSubmitted)) {
      result.push(<AosBadge key="aos-badge" submitted={aosSubmitted} eligible={isEligibleAOS} />)
    }

    if (isPremium) {
      result.push(<PremiumBadge key="premium-badge" />)
    }

    return result
  }, [
    is_end,
    inquirySubmitted,
    isAvailableRequest,
    processingDays,
    isEligibleAOS,
    aosSubmitted,
    isNVCAssociatedCase,
    isPremium,
  ])

  if (badges.length === 0) {
    return '-'
  }

  return <div className={cn('flex flex-wrap gap-[7px]')}>{badges}</div>
}

const ProcessingDaysBadge = ({
  processingDays,
  inquiryEligible,
  caseEnded,
}: {
  processingDays: number
  inquiryEligible: boolean | null
  caseEnded: boolean
}) => {
  const status = caseEnded ? 'success' : inquiryEligible ? 'alert' : 'normal'
  const text = caseEnded ? `Total: ${processingDays} days` : `${processingDays} days`

  return <StatusBadge status={status} text={text} />
}

const CaseInquiryBadge = (props: { submitted: boolean; eligible: boolean | null }) => {
  const badgeStatus = useMemo(() => {
    if (props.submitted) {
      return 'success'
    }

    if (props.eligible) {
      return 'alert'
    }

    return 'normal'
  }, [props.eligible, props.submitted])

  const badgeText = useMemo(() => {
    if (props.submitted) {
      return 'Case Inquiry Submitted'
    }

    if (props.eligible === null) {
      return null
    }

    if (props.eligible) {
      return 'Case Inquiry Eligible'
    }

    return 'Within normal range'
  }, [props.eligible, props.submitted])

  if (!badgeText) {
    return null
  }

  return <StatusBadge status={badgeStatus} text={badgeText} />
}

const AosBadge = (props: { eligible: boolean | null; submitted: boolean }) => {
  const badgeStatus = useMemo(() => {
    if (props.submitted) {
      return 'success'
    }

    return 'alert'
  }, [props.submitted])

  const badgeText = useMemo(() => {
    if (props.submitted) {
      return 'AOS Filed'
    }

    if (props.eligible) {
      return 'AOS Eligible'
    }

    return null
  }, [props.eligible, props.submitted])

  if (!badgeText) {
    return null
  }

  return <StatusBadge status={badgeStatus} text={badgeText} />
}

const PremiumBadge = () => {
  return <StatusBadge status="success" text="Premium Processing" />
}
